import { useEffect, useState } from 'react';
import api from '@/lib/api';
import { toast } from 'sonner';
import { TrendingUp, TrendingDown, Trash2 } from 'lucide-react';
import { useAuthStore } from '@/stores';

const fmt = (n) => `SR ${Number(n || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

/**
 * Salary History with effective dates + reasons (salary_history collection —
 * the same rows the payroll pro-rata engine reads). entityType: "employee" | "partner".
 */
export const SalaryHistoryList = ({ entityType, entityId, refreshKey = 0 }) => {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const user = useAuthStore((s) => s.user);
  const isAdmin = user?.role === 'admin' || user?.is_super_admin;

  const deleteRow = async (id) => {
    if (!window.confirm('Delete this salary history row? Payroll pro-rata will no longer use it.')) return;
    try {
      await api.delete(`/salary-history/${id}`);
      setRows((r) => r.filter((x) => x.id !== id));
      toast.success('History row deleted');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Delete failed');
    }
  };

  useEffect(() => {
    if (!entityId) return undefined;
    let active = true;
    setLoading(true);
    const url = entityType === 'partner' ? `/partners/${entityId}/salary-history` : `/salary-history/${entityId}`;
    api.get(url)
      .then((r) => { if (active) setRows(entityType === 'partner' ? (r.data?.history || []) : (r.data || [])); })
      .catch(() => { if (active) setRows([]); })
      .finally(() => { if (active) setLoading(false); });
    return () => { active = false; };
  }, [entityType, entityId, refreshKey]);

  if (loading) return <p className="text-xs text-muted-foreground py-2" data-testid="salary-history-loading">Loading salary history…</p>;
  if (!rows.length) return <p className="text-xs text-muted-foreground py-2" data-testid="salary-history-empty">No salary changes recorded yet.</p>;

  return (
    <div className="space-y-1.5 max-h-56 overflow-y-auto" data-testid="salary-history-list">
      {rows.map((r) => {
        const up = (r.direction || (r.new_salary >= r.old_salary ? 'increase' : 'decrease')) === 'increase';
        const eff = String(r.effective_date || '').slice(0, 10);
        return (
          <div key={r.id} className="flex items-center justify-between gap-2 text-xs rounded-md border border-border px-2.5 py-1.5" data-testid={`salary-history-row-${r.id}`}>
            <div className="flex items-center gap-2 min-w-0">
              <span className={`inline-flex items-center justify-center w-5 h-5 rounded-full shrink-0 ${up ? 'bg-emerald-100 text-emerald-700' : 'bg-rose-100 text-rose-700'}`}>
                {up ? <TrendingUp size={11} /> : <TrendingDown size={11} />}
              </span>
              <div className="min-w-0">
                <span className="tabular-nums">
                  {fmt(r.old_salary)} <span className="text-muted-foreground">→</span> <span className="font-semibold">{fmt(r.new_salary)}</span>
                </span>
                {r.reason ? <p className="text-[10px] text-muted-foreground truncate">{r.reason}</p> : null}
              </div>
            </div>
            <div className="text-[10px] text-muted-foreground text-right shrink-0">
              <p className="font-medium text-foreground">Effective {eff}</p>
              {r.changed_by_name ? <p>{r.changed_by_name}</p> : null}
            </div>
            {isAdmin && (
              <button
                type="button"
                onClick={() => deleteRow(r.id)}
                className="shrink-0 text-muted-foreground hover:text-rose-600 transition-colors"
                title="Delete this history row (admin only)"
                data-testid={`salary-history-delete-${r.id}`}
              >
                <Trash2 size={13} />
              </button>
            )}
          </div>
        );
      })}
    </div>
  );
};

export default SalaryHistoryList;
